import { supabase, getEdgeFunctionHeaders } from '../supabase';
import { Market } from '../utils/types';

export async function getMarkets(): Promise<Market[]> {
  const { data, error } = await supabase
    .from('markets')
    .select('*')
    .order('name');

  if (error) {
    console.error('Error fetching markets:', error);
    return [];
  }

  return data;
}

export async function getPrimaryMarkets(): Promise<Market[]> {
  const { data, error } = await supabase
    .from('markets')
    .select('*')
    .eq('reviewed', true)
    .is('primary_market_id', null)
    .order('name');

  if (error) {
    console.error('Error fetching primary markets:', error);
    return [];
  }

  return data;
}

export async function getUnreviewedMarketReferences(): Promise<
  { table_name: string; market_id: string; market_name: string; total: number }[]
> {
  const unreviewedMarkets = await supabase
    .from('markets')
    .select('id, name')
    .eq('reviewed', false);

  if (unreviewedMarkets.error || !unreviewedMarkets.data?.length) return [];

  const results: { table_name: string; market_id: string; market_name: string; total: number }[] = [];

  for (const market of unreviewedMarkets.data) {
    const [settMarket, settComparable, tripMarket, tripOrigin] = await Promise.all([
      supabase.from('settlements').select('id', { count: 'exact', head: true }).eq('market_id', market.id),
      supabase.from('settlements').select('id', { count: 'exact', head: true }).eq('comparable_market_id', market.id),
      supabase.from('ship_trips').select('id', { count: 'exact', head: true }).eq('market_id', market.id),
      supabase.from('ship_trips').select('id', { count: 'exact', head: true }).eq('origin_market_id', market.id),
    ]);

    const settlementCount = (settMarket.count || 0) + (settComparable.count || 0);
    const tripCount = (tripMarket.count || 0) + (tripOrigin.count || 0);

    if (settlementCount > 0) {
      results.push({ table_name: 'Liquidaciones', market_id: market.id, market_name: market.name, total: settlementCount });
    }
    if (tripCount > 0) {
      results.push({ table_name: 'Viajes', market_id: market.id, market_name: market.name, total: tripCount });
    }
  }

  return results;
}

export async function getMarketById(id: number): Promise<Market | null> {
  const { data, error } = await supabase
    .from('markets')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching market:', error);
    return null;
  }

  return data;
}

export async function createMarket(
  market: Omit<Market, 'id' | 'created_at'>
): Promise<Market | null> {
  const { data, error } = await supabase
    .from('markets')
    .insert([market])
    .select()
    .single();

  if (error) {
    console.error('Error creating market:', error);
    return null;
  }

  return data;
}

export interface SuggestedMarket {
  id: string;
  name: string;
  country: string;
  country_code: string;
}

export async function suggestMarkets(
  locodes: string[]
): Promise<SuggestedMarket[]> {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/suggest-markets`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getEdgeFunctionHeaders()),
      },
      body: JSON.stringify({ locodes }),
    }
  );

  const payload = await response.json();
  if (!response.ok || !payload.success) {
    throw new Error(payload.error || 'Failed to suggest markets');
  }

  return payload.markets || [];
}

export async function updateMarket(
  id: number,
  market: Partial<Market>
): Promise<Market | null> {
  const { data, error } = await supabase
    .from('markets')
    .update(market)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating market:', error);
    return null;
  }

  return data;
}

export async function deleteMarket(id: number): Promise<boolean> {
  const { error } = await supabase.from('markets').delete().eq('id', id);

  if (error) {
    console.error('Error deleting market:', error);
    return false;
  }

  return true;
} 
